import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User } from '../types';
import { STORAGE_KEYS } from '../utils/constants';
import { authService } from '../services/authService';
import { profileKeys } from '../hooks/useProfile';
import { userService, UserProfile } from '../services/userService';

interface AuthContextType {
  user: User | null;
  originalUser: User | null; // Оригинальный пользователь при входе под другим
  login: (user: User, token: string, originalUser?: User) => void;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  stopImpersonating: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const login = useCallback((userData: User, token: string, originalUserData?: User) => {
    // Очищаем кеш перед входом нового пользователя
    queryClient.clear();
    
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    setUser(userData);
    
    if (originalUserData) {
      localStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, JSON.stringify(originalUserData));
      setOriginalUser(originalUserData);
    }
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      // ВАЖНО: Сначала вызываем API logout, чтобы обновить статус сессии в истории
      // Токен еще нужен для авторизации запроса
      await authService.logout();
    } catch (error) {
      // Игнорируем ошибки, так как токен может быть уже недействителен
      console.error('Ошибка при выходе:', error);
    } finally {
      // Очищаем весь кеш React Query перед выходом
      queryClient.clear();
      
      // Удаляем токен и данные пользователя из localStorage только после попытки вызова API
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
      localStorage.removeItem(STORAGE_KEYS.PREFETCH_COMPLETED);
      setUser(null);
      setOriginalUser(null);
    }
  }, [queryClient]);

  const stopImpersonating = useCallback(async () => {
    if (!originalUser) {
      throw new Error('Оригинальный пользователь не найден');
    }
    
    try {
      const response = await authService.stopImpersonating(originalUser.id);
      const userData: User = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        role: 'user',
      };
      login(userData, response.token);
      // Очищаем информацию об оригинальном пользователе
      localStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
      setOriginalUser(null);
    } catch (error) {
      console.error('Ошибка при возврате к своему аккаунту:', error);
      throw error;
    }
  }, [originalUser, login]);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    const storedOriginalUser = localStorage.getItem(STORAGE_KEYS.ORIGINAL_USER);

    if (!token) {
      setLoading(false);
      return;
    }

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    }

    if (storedOriginalUser) {
      try {
        setOriginalUser(JSON.parse(storedOriginalUser));
      } catch {
        setOriginalUser(null);
      }
    }

    const restoreSession = async () => {
      try {
        // Сначала проверяем, есть ли профиль в кеше React Query (быстрее)
        const cachedProfile = queryClient.getQueryData<UserProfile>(profileKeys.profile());
        
        let userData: User;
        if (cachedProfile) {
          // Используем данные из кеша для мгновенного отображения
          userData = {
            id: cachedProfile.id,
            name: cachedProfile.name,
            email: cachedProfile.email,
            avatar: cachedProfile.avatar || undefined,
            role: 'user',
          };
          setUser(userData);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        }
        
        // Загружаем полный профиль с сервера (включая is_system_admin и is_user_manager)
        const profile = await userService.getProfile();
        
        // Обновляем данные пользователя
        userData = {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          avatar: profile.avatar || undefined,
          role: 'user',
        };
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        setUser(userData);
        
        // Сохраняем полный профиль в кеш React Query для мгновенного доступа в Header и Admin
        queryClient.setQueryData(profileKeys.profile(), profile);
      } catch {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, [queryClient]);

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      const updatedUser = { ...prevUser, ...userData };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        originalUser,
        login,
        logout,
        updateUser,
        stopImpersonating,
        isAuthenticated: !!user,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

